import { convertToMillion } from "./index";

// Single source of truth for building the hospitals-list backend URL.
//
// Every filter already lives in the URL query string (see useFilters). This
// pure builder derives the matching backend fetch/export URL on demand from a
// plain descriptor, so no component needs to persist the built URL anywhere
// (previously it was duplicated into localStorage `url` / `searchURL`).
//
// Endpoint precedence mirrors the historical LeftSection.fetchHospitalData
// routing: bookmark > text search > state filter > advanced filter > all.

const baseUrl = process.env.REACT_APP_BASE_URL;

// Frontend advanced-filter field names -> backend query keys. Only
// `facility_type` actually differs; the rest are pass-through but listed so the
// mapping is explicit and matches FilterComp.constructApiUrl.
const ADVANCED_PARAM_MAPPING = {
  facility_type: "fac_type",
  fac_type: "fac_type",
  bed_count_min: "bed_count_min",
  bed_count_max: "bed_count_max",
  square_footage_min: "square_footage_min",
  square_footage_max: "square_footage_max",
  patient_discharge_min: "patient_discharge_min",
  patient_discharge_max: "patient_discharge_max",
  hospital_name: "hospital_name",
  cbsa: "cbsa",
  county: "county",
  zip_code: "zip_code",
  coc_accredited: "coc_accredited",
  urban_rural_flag: "urban_rural_flag",
  prov_control_type: "prov_control_type",
  net_patient_revenue_min: "net_patient_revenue_min",
  net_patient_revenue_max: "net_patient_revenue_max",
};

const hasRevenueValue = (v) =>
  v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v));

// Normalizes URL-derived advanced filters (frontend keys, raw form values) into
// backend query params: converts net patient revenue to absolute dollars and
// remaps field names. Mirrors FilterComp.constructApiUrl so a URL-driven
// pagination/export request matches the original filtered fetch exactly.
export const normalizeAdvancedFilters = (advancedFilters = {}) => {
  let source = { ...advancedFilters };
  if (
    hasRevenueValue(source.net_patient_revenue_min) ||
    hasRevenueValue(source.net_patient_revenue_max)
  ) {
    source = convertToMillion(source);
  }

  const normalized = {};
  Object.entries(source).forEach(([key, value]) => {
    const isNaNValue = typeof value === "number" && Number.isNaN(value);
    if (value === undefined || value === null || value === "" || isNaNValue) {
      return;
    }
    const backendKey = ADVANCED_PARAM_MAPPING[key] || key;
    normalized[backendKey] = value;
  });
  return normalized;
};

// Builds a hospitals backend URL from a filter descriptor.
//
// - search: applied search term (`q`). Routes to /search only when no state and
//   no advanced filters are active (a state search sets both `q` and `state`,
//   which correctly routes to the state-filter endpoint).
// - states: resolved state abbreviations (explicit selection or legend-derived).
// - advancedFilters: URL-derived advanced fields (frontend-keyed, raw values).
// - forExport: swap the endpoint to /export and drop pagination params.
export function buildHospitalsUrl({
  search = null,
  states = [],
  advancedFilters = {},
  isBookmark = false,
  partnerExclusiveFlag = true,
  globalFilter = "all",
  quickFilter = null,
  sortBy = "ranking",
  sortOrder = "asc",
  page = null,
  perPage = null,
  forExport = false,
} = {}) {
  const globalFilterValue =
    typeof globalFilter === "object" && globalFilter
      ? globalFilter.value
      : globalFilter;
  const sortByValue = (sortBy || "ranking").toLowerCase();
  const cleanStates = (states || []).filter(Boolean);
  const advanced = normalizeAdvancedFilters(advancedFilters);
  const hasAdvancedFilters = Object.keys(advanced).length > 0;
  const isTextSearch =
    !!search && cleanStates.length === 0 && !hasAdvancedFilters && !isBookmark;

  const params = new URLSearchParams();
  let path;

  if (isBookmark) {
    path = forExport ? "/bookmark/bookmarks/export" : "/bookmark/bookmarks";
  } else if (isTextSearch) {
    path = forExport ? "/new_data/hospitals/export" : "/new_data/hospitals/search";
    params.set("search_term", search);
  } else if (cleanStates.length > 0 && !hasAdvancedFilters) {
    path = forExport ? "/new_data/hospitals/export" : "/new_data/hospitals/filter";
    params.set("states", cleanStates.join(","));
  } else if (hasAdvancedFilters) {
    path = forExport ? "/new_data/hospitals/export" : "/new_data/hospitals/filter";
    Object.entries(advanced).forEach(([key, value]) => {
      params.set(key, String(value));
    });
    // An advanced-filter set can also carry a selected state.
    if (cleanStates.length > 0) params.set("states", cleanStates.join(","));
  } else {
    path = forExport ? "/new_data/hospitals/export" : "/new_data/hospitals/all";
  }

  // Base filters applied to every endpoint.
  params.set("partnerExclusiveArea", String(partnerExclusiveFlag));
  params.set("globalFilter", globalFilterValue || "all");
  if (quickFilter) params.set("quickFilter", quickFilter);
  params.set("sort_by", sortByValue);
  params.set("sort_order", sortOrder || "asc");

  // Pagination for list endpoints (/all, /filter, /search). Bookmark returns the
  // full set in one request, so callers omit page/perPage there. Export drops
  // pagination regardless of endpoint.
  if (!forExport) {
    if (page !== null && page !== undefined) params.set("page", String(page));
    if (perPage !== null && perPage !== undefined) {
      params.set("per_page", String(perPage));
    }
  }

  return `${baseUrl}${path}?${params.toString()}`;
}

export default buildHospitalsUrl;
